import { columnWeights, MAX_COLUMNS } from '../../../core/layout/columns';
import { collectDescendants, findNodeParent, insertNode, removeNode } from './canvasOperations';

const numberedSlots = node => {
  const slots = Object.entries(node?.slots || {}).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
  return slots.length && slots.every(([key, ids], index) => key === `col-${index + 1}` && Array.isArray(ids)) ? slots : null;
};
const withTracks = (node, tracks, weights, managed) => ({
  ...node,
  ...((managed || !node.slots) ? { editorCreatedSlots: tracks.map((_, i) => `col-${i + 1}`).filter((_, i) => managed ? managed[i] : true) } : {}),
  props: { ...node.props, columns: tracks.length, template: '', ratio: weights.map(String) },
  slots: Object.fromEntries(tracks.map((ids, index) => [`col-${index + 1}`, ids])),
});

/** Remove only a track emptied by this edit; leave unrelated preset slots and CSS intact. */
const compactSource = (nodes, rootId, source, movedId) => {
  if (!source) return nodes;
  const node = nodes.find(item => item.id === source.parentId);
  if (node?.type !== 'columns') return nodes;
  if (node.editorCreatedSlots && !node.editorCreatedSlots.includes(source.slotKey)) return nodes;
  const slots = numberedSlots(node);
  const weights = columnWeights(node);
  if (!slots || !weights) return nodes;
  // Inserting a sibling can renumber slots. Find the original track by its remaining children.
  const remaining = source.children.filter(id => id !== movedId);
  if (remaining.length) return nodes;
  const index = slots.findIndex(([key, ids]) => key === source.slotKey && !ids.length);
  if (index < 0) return nodes;
  if (slots.length === 1) {
    if (node.id === rootId) return nodes;
    const parent = findNodeParent(nodes, node.id);
    return compactSource(removeNode(nodes, rootId, node.id), rootId, parent, node.id);
  }
  return nodes.map(item => item.id === node.id
    ? withTracks(node, slots.filter((_, i) => i !== index).map(([, ids]) => ids), weights.filter((_, i) => i !== index), node.editorCreatedSlots ? slots.filter((_, i) => i !== index).map(([key]) => node.editorCreatedSlots.includes(key)) : undefined) : item);
};

/** Horizontal placement uses the existing columns contract; it is one undoable graph edit. */
export const placeCanvasNode = (nodes, rootId, position, nodeOrId, makeId) => {
  const moving = typeof nodeOrId === 'string';
  const id = moving ? nodeOrId : nodeOrId?.id;
  const source = moving ? findNodeParent(nodes, id) : null;
  if (!['left', 'right'].includes(position?.position)) {
    const next = insertNode(nodes, rootId, position, nodeOrId);
    return next === nodes ? nodes : compactSource(next, rootId, source, id);
  }
  // Reuse insertion validation for missing targets, self moves, roots and descendant cycles.
  const inserted = insertNode(nodes, rootId, { ...position, position: 'before' }, nodeOrId);
  if (inserted === nodes) return nodes;
  const targetParent = findNodeParent(inserted, position.nodeId);
  const parent = inserted.find(node => node.id === targetParent.parentId);
  const slots = parent.type === 'columns' && numberedSlots(parent);
  const weights = slots && columnWeights(parent);
  const siblings = targetParent.children.filter(child => child !== id);
  let next;
  if (slots && weights && siblings.length === 1) {
    // A whole track is one block: extend that row instead of nesting another grid.
    const tracks = slots.map(([, ids]) => ids.filter(child => child !== id));
    const targetIndex = slots.findIndex(([key]) => key === targetParent.slotKey);
    const insertAt = targetIndex + (position.position === 'right' ? 1 : 0);
    tracks.splice(insertAt, 0, [id]);
    // Reusing an emptied source track preserves its width and avoids a blank area.
    const sourceIndex = source?.parentId === parent.id && source.children.length === 1
      ? slots.findIndex(([key]) => key === source.slotKey) : -1;
    const managed = parent.editorCreatedSlots ? slots.map(([key]) => parent.editorCreatedSlots.includes(key)) : null;
    if (managed) managed.splice(insertAt, 0, sourceIndex >= 0 ? managed[sourceIndex] : true);
    const nextWeights = [...weights];
    nextWeights.splice(insertAt, 0, weights[sourceIndex >= 0 ? sourceIndex : targetIndex]);
    if (sourceIndex >= 0) {
      const shifted = sourceIndex + (sourceIndex >= insertAt ? 1 : 0);
      tracks.splice(shifted, 1); nextWeights.splice(shifted, 1);
      if (managed) managed.splice(shifted, 1);
    }
    if (tracks.length > MAX_COLUMNS) return nodes;
    next = inserted.map(node => node.id === parent.id ? withTracks(parent, tracks, nextWeights, managed) : node);
    return source?.parentId === parent.id ? next : compactSource(next, rootId, source, id);
  }
  // Other blocks in this track stay in place; only the target shares its row with the drop.
  const wrapperId = makeId?.('columns');
  if (!wrapperId || inserted.some(node => node.id === wrapperId)) return nodes;
  const pair = position.position === 'left' ? [id, position.nodeId] : [position.nodeId, id];
  const wrapper = withTracks({ id: wrapperId, type: 'columns', props: { gap: 16 } }, pair.map(child => [child]), [1, 1]);
  next = inserted.map(node => node.id === parent.id ? {
    ...node, slots: { ...node.slots, [targetParent.slotKey]: node.slots[targetParent.slotKey]
      .filter(child => child !== id).map(child => child === position.nodeId ? wrapperId : child) },
  } : node);
  return compactSource([...next, wrapper], rootId, source, id);
};

export const removeCanvasNode = (nodes, rootId, id) => {
  const next = removeNode(nodes, rootId, id);
  return next === nodes ? nodes : compactSource(next, rootId, findNodeParent(nodes, id), id);
};


/** Canvas-edge placement extends the outer layout, independently of the nearest block. */
export const placeCanvas = (canvas, position, nodeOrId, makeId) => {
  if (!canvas) return canvas;
  const { nodes, root: rootId } = canvas;
  if (position?.scope !== 'canvas') {
    const next = placeCanvasNode(nodes, rootId, position, nodeOrId, makeId);
    return next === nodes ? canvas : { ...canvas, nodes: next };
  }
  if (!['left', 'right'].includes(position.position) || position.nodeId !== rootId) return canvas;
  const rootNode = nodes.find(item => item.id === rootId);
  const moving = typeof nodeOrId === 'string';
  const id = moving ? nodeOrId : nodeOrId?.id;
  const existing = nodes.find(item => item.id === id);
  if (!rootNode || !id || id === rootId || (moving ? !existing : existing)
    || (moving && collectDescendants(nodes, id).has(rootId))) return canvas;
  const source = moving ? findNodeParent(nodes, id) : null;
  const slots = rootNode.type === 'columns' && numberedSlots(rootNode);
  const weights = slots && columnWeights(rootNode);
  // Detach the moved subtree without deleting its descendants.
  let next = nodes.map(item => ({ ...item, ...(item.slots ? {
    slots: Object.fromEntries(Object.entries(item.slots).map(([key, children]) => [
      key, Array.isArray(children) ? children.filter(child => child !== id) : children,
    ])),
  } : {}) }));
  if (!moving) next.push(nodeOrId);
  if (slots && weights) {
    const tracks = slots.map(([, children]) => children.filter(child => child !== id));
    const sourceIndex = source?.parentId === rootId && source.children.length === 1
      ? slots.findIndex(([key]) => key === source.slotKey) : -1;
    const managed = rootNode.editorCreatedSlots ? slots.map(([key]) => rootNode.editorCreatedSlots.includes(key)) : null;
    const movedManaged = sourceIndex >= 0 ? managed?.[sourceIndex] : true;
    const nextWeights = [...weights];
    const newWeight = sourceIndex >= 0 ? weights[sourceIndex] : weights.reduce((sum, value) => sum + value, 0) / weights.length;
    if (sourceIndex >= 0) {
      if (sourceIndex === (position.position === 'left' ? 0 : slots.length - 1)) return canvas;
      tracks.splice(sourceIndex, 1); nextWeights.splice(sourceIndex, 1);
      if (managed) managed.splice(sourceIndex, 1);
    }
    if (tracks.length >= MAX_COLUMNS) return canvas;
    const index = position.position === 'left' ? 0 : tracks.length;
    tracks.splice(index, 0, [id]); nextWeights.splice(index, 0, newWeight);
    if (managed) managed.splice(index, 0, movedManaged);
    next = next.map(item => item.id === rootId ? withTracks(item, tracks, nextWeights, managed) : item);
    if (source?.parentId !== rootId) next = compactSource(next, rootId, source, id);
    return { ...canvas, nodes: next };
  }
  // Preserve arbitrary CSS and non-column roots by wrapping the whole existing layout.
  const wrapperId = makeId?.('columns');
  if (!wrapperId || next.some(item => item.id === wrapperId)) return canvas;
  next = compactSource(next, rootId, source, id);
  const pair = position.position === 'left' ? [id, rootId] : [rootId, id];
  const wrapper = withTracks({ id: wrapperId, type: 'columns', props: { gap: 16 } }, pair.map(child => [child]), [1, 1]);
  return { ...canvas, root: wrapperId, nodes: [...next, wrapper] };
};
